import { v4 as uuidv4 } from 'uuid';

import { CardPaymentHooks, FUNC_BUTTONS } from 'paymentIntegrations/types';
import { getClientCode } from 'reducers/Login';
import { getCardPaymentsForIntegration } from 'reducers/Payments';
import { lastInvoiceNumbers } from 'services/localDB';
import { getSelectedPosID } from 'reducers/PointsOfSale';
import { getCurrentSalesDocument } from 'reducers/sales';
import { ErplyAttributes, sleep } from 'utils';
import { toPaymentObj } from 'paymentIntegrations/pax/handlers/toPaymentObj';
import { addSuccess } from 'actions/Error';
import { getErrorMessage, withWaitingForTerminal } from 'paymentIntegrations';
import { setPayment } from 'actions/Payments';

import { checkTransactionStatus, makePayment } from '../api2';

import { handleFail } from './handleFail';

export enum TransactionType {
  Sale = 'Sale',
  Refund = 'Refund',
  Void = 'Void',
}

export const generateInputData = (
  payment,
  initialTransactionType?: TransactionType,
) => async (dispatch, getState) => {
  const clientCode = getClientCode(getState());
  const pointOfSaleID = getSelectedPosID(getState());
  const lastInvoiceNumber = Number(
    (await lastInvoiceNumbers.getItem({
      key: pointOfSaleID,
    })) ?? String(new Date().getTime()),
  );
  const currentSale = getCurrentSalesDocument(getState());

  const paymentAttributes = new ErplyAttributes(payment.attributes);

  const hostReferenceNumber =
    paymentAttributes.get('hostReferenceNumber') ?? uuidv4();
  const referenceNumber = paymentAttributes.get('refNo') ?? uuidv4();
  const authCode = paymentAttributes.get('authCode');

  let ecrReferenceNumber = paymentAttributes.get('ecrReferenceNumber');
  if (!ecrReferenceNumber) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        'This should never happen: pax generating payment data, expects ecrReferenceNumber to already be generated, but it is missing',
      );
    } else {
      ecrReferenceNumber = String(new Date().getTime());
    }
  }

  const isNegativeAmount = Number(payment.amount) < 0;

  const transactionType = (() => {
    if (initialTransactionType) return initialTransactionType;
    if (payment.paid && payment.shouldProcess) return TransactionType.Void;
    if (isNegativeAmount) return TransactionType.Refund;
    return TransactionType.Sale;
  })();

  const commonProps = {
    requestID: uuidv4(),
    clientCode,
    ecrReferenceNumber,
  };

  switch (transactionType) {
    case TransactionType.Sale:
      return {
        transactionType: TransactionType.Sale,
        invoiceNumber: String(lastInvoiceNumber + 1),
        amount: payment.amount,
        ...commonProps,
      };
    case TransactionType.Refund:
      return {
        transactionType: TransactionType.Refund,
        invoiceNumber: `${lastInvoiceNumber + 1}K`,
        referenceNumber,
        amount: payment.amount,
        authCode,
        ...commonProps,
      };
    case TransactionType.Void:
      return {
        transactionType: TransactionType.Void,
        invoiceNumber: currentSale?.number ?? String(lastInvoiceNumber + 1),
        referenceNumber,
        hostReferenceNumber,
        authCode,
        ...commonProps,
      };
    default:
      throw new Error('This should not happen');
  }
};

let latestProcessed: null | {
  payment: any;
  request: any;
  response?: any;
} = null;
export const processPayments = ({
  beforeDocDelete,
  beforeDocSave,
  updateMessage,
}: CardPaymentHooks) => async (dispatch, getState) => {
  const cardPayments = getCardPaymentsForIntegration('pax')(getState());
  const shouldVoid = cardPayments.every(cp => cp.paid && cp.shouldProcess);

  const ecrReferenceNumber = new Date().getTime();
  return cardPayments
    .filter(p => (shouldVoid ? p.paid : !p.paid))
    .reduce(async (prevPayment, payment, i) => {
      // Create a copy to fix "Assignment to property of function parameter" lint error
      const currentPayment = payment;
      // Ensure all payments have ecrReferenceNumber
      const attrs = new ErplyAttributes(currentPayment.attributes);
      if (!attrs.get('ecrReferenceNumber')) {
        attrs.set('ecrReferenceNumber', String(ecrReferenceNumber + i));
        // Mutate the payment so that the main body sees the updated value
        currentPayment.attributes = attrs.asDict;
        // But also inform the POS to ensure this value will persist
        // NB: Not 'beforeDocSave' because that comes with a "Payment success" banner
        await dispatch(setPayment(currentPayment));
      }
      // Then start processing them
      return prevPayment.then(async ({ data, errors }) => {
        if (!errors.length) {
          await dispatch(
            withWaitingForTerminal(async () => {
              try {
                updateMessage(`Processing payment of ${currentPayment.amount}`);
                const inputData = await dispatch(
                  generateInputData(currentPayment),
                );
                latestProcessed = {
                  payment: currentPayment,
                  request: inputData,
                };
                const {
                  records: [record],
                } = await makePayment(inputData);
                latestProcessed.response = record;

                if (record.resultCode === '0') {
                  latestProcessed = null;
                  if (shouldVoid) {
                    beforeDocDelete(currentPayment.key);
                    dispatch(addSuccess('Void successful'));
                  } else {
                    await beforeDocSave({
                      ...currentPayment,
                      ...record,
                      ...toPaymentObj(record),
                    });
                  }
                  data.push(record);
                } else {
                  await dispatch(
                    handleFail({
                      record,
                      updateMessage,
                      payment: currentPayment,
                    }),
                  );
                  errors.push(record.statusMessage);
                }
              } catch (error) {
                updateMessage(
                  getErrorMessage(error, 'Payment processing failed'),
                );
                console.error(error);
                errors.push(error);
              } finally {
                await sleep(1);
              }
            }),
          );
        }

        return { data, errors };
      });
    }, Promise.resolve({ data: [], errors: [] }));
};

export function recoverLatestAndContinue(hooks: CardPaymentHooks) {
  return async function action(dispatch, getState) {
    if (!latestProcessed) throw new Error('No payment to recover');
    const { payment, request, response } = latestProcessed;
    hooks.updateMessage(
      `${response?.transactionStatus ?? ''}\nChecking transaction status...`,
    );
    hooks.enableButtons([FUNC_BUTTONS.CANCEL]);
    const {
      records: [record],
    } = await checkTransactionStatus({
      clientCode: request.clientCode,
      requestID: uuidv4(),
      ecrReferenceNumber: request.ecrReferenceNumber,
    } as any);
    // Code 1 - Duplicate transaction.
    if (record.resultCode === '1') {
      // It's the correct one, so rewrite the statuscode to 0 so it can be processed as a success
      if (
        String(request.ecrReferenceNumber) === String(record.ecrReferenceNumber)
      ) {
        record.resultCode = '0';
      }
    }

    if (record.resultCode === '0') {
      hooks.updateMessage(
        `${response?.transactionStatus}\nChecking transaction status: OK`,
      );
      if (payment.paid && payment.shouldProcess) {
        // Succeeded, and were trying to void. Delete payment and continue
        hooks.beforeDocDelete(payment.key);
        dispatch(addSuccess('Void successful'));
        await dispatch(processPayments(hooks));
        return true;
      }
      // Succeeded, and were trying to pay. Save payment and continue
      await hooks.beforeDocSave({
        ...payment,
        ...record,
        ...toPaymentObj(record),
      });
      await dispatch(processPayments(hooks));
      return true;
    }
    // Transaction not successful. Show message and unlock buttons
    hooks.updateMessage(
      `${response?.transactionStatus}\nChecking transaction status: ${record.transactionStatus}`,
    );
    hooks.enableButtons(hooks.enabledButtons);
    return false;
  };
}
export function voidLatest(hooks: CardPaymentHooks) {
  return async function action(dispatch, getState) {
    if (!latestProcessed) throw new Error('No payment to void');
    const { payment, request, response } = latestProcessed;
    const inputData = await dispatch(
      generateInputData(payment, TransactionType.Void),
    );
    hooks.updateMessage('Voiding transaction...');

    inputData.hostReferenceNumber = response?.hostReferenceNumber;
    inputData.referenceNumber = response?.referenceNumber;
    try {
      const {
        records: [result],
      } = await makePayment(inputData);
      // If void fails, it's likely the original transaction just didn't go through.
      // So let's just assume that since the void request went through (didn't give a http level error)
      // that the original transaction no longer exists.
      latestProcessed = null;
      hooks.rejectPayments();
    } catch (e) {
      console.warn('Void request has failed with http error', e);
      hooks.rejectPayments(); // idk why but voiding always fails. The previous version of the code would always reject too
    }
  };
}
